/**
 * GIWAHS — Provider-Agnostic Analytics (privacy-first)
 * ---------------------------------------------------------------------
 * Design:
 *   • Adapter pattern. Providers: 'none' (default) | 'console' | 'posthog' | 'plausible'
 *   • Default in development → 'none' (no events fired).
 *   • Honors navigator.doNotTrack — events are silently dropped if user opts out.
 *   • No automatic user identification. No persistent IDs.
 *   • PII (email, phone) is auto-stripped from event properties.
 *   • Third-party scripts (PostHog SDK) are loaded lazily *only* when the
 *     provider is configured and a key is present — never speculatively.
 *   • Plausible mode uses image-pixel transport — no JS SDK needed.
 *
 * Env vars:
 *   REACT_APP_ANALYTICS_PROVIDER   posthog | plausible | console | none
 *   REACT_APP_ANALYTICS_KEY        PostHog project key OR Plausible site domain
 *   REACT_APP_ANALYTICS_HOST       (optional) self-hosted endpoint
 *                                  PostHog default: https://app.posthog.com
 *                                  Plausible default: https://plausible.io
 *   REACT_APP_ANALYTICS_DEBUG      "1" to enable analytics in development
 */

const PROVIDER = process.env.REACT_APP_ANALYTICS_PROVIDER || 'none';
const KEY      = process.env.REACT_APP_ANALYTICS_KEY || '';
const HOST     = process.env.REACT_APP_ANALYTICS_HOST || '';
const DEBUG    = process.env.REACT_APP_ANALYTICS_DEBUG === '1';
const IS_PROD  = process.env.NODE_ENV === 'production';

// ─── PII guards ────────────────────────────────────────────────────────
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_RE = /\+?\d[\d\s().-]{7,}\d/g;

const stripPII = (val) => {
  if (val == null) return val;
  if (typeof val === 'string') {
    return val.replace(EMAIL_RE, '[email]').replace(PHONE_RE, '[phone]');
  }
  if (typeof val === 'object' && !Array.isArray(val)) {
    const out = {};
    for (const [k, v] of Object.entries(val)) {
      // Reject obvious PII property names outright.
      if (/email|phone|whatsapp|tel/i.test(k)) continue;
      out[k] = stripPII(v);
    }
    return out;
  }
  if (Array.isArray(val)) return val.map(stripPII);
  return val;
};

// ─── DNT + enabled guards ──────────────────────────────────────────────
const respectsDoNotTrack = () => {
  if (typeof navigator === 'undefined') return false;
  const dnt = navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack;
  return dnt === '1' || dnt === 'yes';
};

const isEnabled = () => {
  if (PROVIDER === 'none') return false;
  if (!IS_PROD && !DEBUG) return false;             // disabled in dev unless DEBUG flag set
  if (respectsDoNotTrack()) return false;
  if ((PROVIDER === 'posthog' || PROVIDER === 'plausible') && !KEY) return false;
  return true;
};

// ─── Provider adapters ────────────────────────────────────────────────
const adapters = {
  none: {
    init: () => {},
    track: () => {},
    pageview: () => {},
  },

  console: {
    init: () => console.info('[GIWAHS analytics] console adapter — events log to console only.'),
    track: (event, props) => console.info('[analytics:track]', event, props),
    pageview: (path) => console.info('[analytics:pageview]', path),
  },

  /**
   * PostHog — loaded lazily. Project key required. Cookie-free by default
   * (persistence: 'memory') for privacy-conscious operation.
   */
  posthog: {
    _client: null,
    async _load() {
      if (this._client) return this._client;
      if (typeof window === 'undefined') return null;
      try {
        const mod = await import(/* webpackChunkName: "posthog" */ 'posthog-js');
        const posthog = mod.default || mod;
        posthog.init(KEY, {
          api_host: HOST || 'https://app.posthog.com',
          autocapture: false,            // never autocapture clicks/forms
          capture_pageview: false,       // we drive pageviews ourselves
          disable_session_recording: true,
          persistence: 'memory',         // no cookies; no localStorage IDs
          opt_out_capturing_by_default: false,
          loaded: (p) => p.opt_out_persistence?.(),
        });
        this._client = posthog;
        return posthog;
      } catch (e) {
        console.warn('[GIWAHS analytics] posthog SDK failed to load:', e?.message);
        return null;
      }
    },
    async init()  { await this._load(); },
    async track(event, props) {
      const p = await this._load();
      p && p.capture(event, props);
    },
    async pageview(path) {
      const p = await this._load();
      p && p.capture('$pageview', { $current_url: window.location.origin + path });
    },
  },

  /**
   * Plausible — cookieless and SDK-less. Uses /api/event endpoint via
   * sendBeacon (or fetch with keepalive fallback). Page views fire as
   * the 'pageview' event per Plausible spec.
   */
  plausible: {
    _host() { return HOST || 'https://plausible.io'; },
    _send(event, props) {
      const payload = {
        name: event,
        url:  window.location.href,
        domain: KEY,                  // Plausible "site domain"
        referrer: document.referrer || '',
        props: props || {},
      };
      const body = JSON.stringify(payload);
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(`${this._host()}/api/event`, new Blob([body], { type: 'application/json' }));
        } else {
          fetch(`${this._host()}/api/event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
            keepalive: true,
          });
        }
      } catch (e) {
        // swallow — analytics never breaks the app
      }
    },
    init() {},
    track(event, props) { this._send(event, props); },
    pageview(path)      { this._send('pageview', { path }); },
  },
};

const activeAdapter = adapters[PROVIDER] || adapters.none;

// ─── Public API ───────────────────────────────────────────────────────
let _initialized = false;
export const initAnalytics = () => {
  if (_initialized) return;
  _initialized = true;
  if (!isEnabled()) return;
  activeAdapter.init?.();
};

/**
 * Track an event. Properties are PII-stripped before transport.
 * Common event names live in EVENTS below — prefer named constants.
 */
export const track = (event, properties = {}) => {
  if (!isEnabled()) return;
  try {
    activeAdapter.track(event, stripPII({ ...properties, ts: Date.now() }));
  } catch {
    // analytics must never throw
  }
};

export const trackPageview = (path) => {
  if (!isEnabled()) return;
  try {
    activeAdapter.pageview(path);
  } catch {}
};

/**
 * Stable event names. Centralised so they can be referenced symbolically
 * across the codebase and not drift via typos.
 */
export const EVENTS = {
  // Navigation
  PAGE_VIEWED:               'page_viewed',

  // Discovery / content
  SUPPLIER_VIEWED:           'supplier_viewed',
  PRODUCT_VIEWED:            'product_viewed',
  GUIDE_VIEWED:              'guide_viewed',
  GUIDE_CTA_CLICKED:         'guide_cta_clicked',
  CATEGORY_LANDING_VIEWED:   'category_landing_viewed',
  COUNTRY_LANDING_VIEWED:    'country_landing_viewed',

  // Search & filter
  FACTORY_SEARCH:            'factory_search',
  PRODUCT_SEARCH:            'product_search',
  GLOBAL_SEARCH:             'global_search',
  FILTER_APPLIED:            'filter_applied',

  // Lead capture
  RFQ_STARTED:               'rfq_started',
  RFQ_SUBMITTED:             'rfq_submitted',
  CONTACT_SUBMITTED:         'contact_submitted',
  SUPPLIER_APP_STARTED:      'supplier_app_started',
  SUPPLIER_APP_SUBMITTED:    'supplier_app_submitted',

  // Saved / compare
  SUPPLIER_SAVED:            'supplier_saved',
  SUPPLIER_UNSAVED:          'supplier_unsaved',
  SUPPLIER_COMPARED:         'supplier_compared',
  SUPPLIER_UNCOMPARED:       'supplier_uncompared',

  // AI surfaces
  AI_IMAGE_UPLOADED:         'ai_image_uploaded',
  AI_IMAGE_SEARCH_COMPLETED: 'ai_image_search_completed',
  AI_ASSISTANT_MESSAGE_SENT: 'ai_assistant_message_sent',

  // AI Image Search (Gemini 3 Pro Vision)
  IMAGE_SEARCH_STARTED:          'image_search_started',
  IMAGE_SEARCH_COMPLETED:        'image_search_completed',
  IMAGE_SEARCH_FAILED:           'image_search_failed',
  IMAGE_RESULT_FACTORY_CLICKED:  'image_result_factory_clicked',
  IMAGE_RESULT_RFQ_STARTED:      'image_result_rfq_started',

  // Sourcing Assistant (Claude Sonnet 4.5)
  ASSISTANT_CHAT_STARTED:        'assistant_chat_started',
  ASSISTANT_MESSAGE_SENT:        'assistant_message_sent',
  ASSISTANT_REPLY_RECEIVED:      'assistant_reply_received',
  ASSISTANT_REPLY_FAILED:        'assistant_reply_failed',
  ASSISTANT_RFQ_CREATED:         'assistant_rfq_created',
  ASSISTANT_SUPPLIER_VIEWED:     'assistant_supplier_viewed',
  ASSISTANT_COMPARE_OPENED:      'assistant_compare_opened',
  ASSISTANT_CHAT_CLEARED:        'assistant_chat_cleared',

  // Buyer-side quote visibility (Blocker 1 fix)
  BUYER_QUOTE_VIEWED:            'buyer_quote_viewed',
  BUYER_QUOTE_SUPPLIER_CLICKED:  'buyer_quote_supplier_clicked',
  BUYER_QUOTE_ACCEPTED:          'buyer_quote_accepted',
  BUYER_QUOTE_REJECTED:          'buyer_quote_rejected',
  BUYER_QUOTE_DECISION_FAILED:   'buyer_quote_decision_failed',

  // Supplier verification (P4)
  SUPPLIER_VERIFICATION_STARTED:    'supplier_verification_started',
  SUPPLIER_VERIFICATION_SUBMITTED:  'supplier_verification_submitted',
  SUPPLIER_DOC_UPLOADED:            'supplier_doc_uploaded',
  SUPPLIER_VERIFICATION_APPROVED:   'supplier_verification_approved',
  SUPPLIER_VERIFICATION_REJECTED:   'supplier_verification_rejected',
  SUPPLIER_VERIFICATION_REVOKED:    'supplier_verification_revoked',
  SUPPLIER_VERIFICATION_PAID:       'supplier_verification_paid',
  SUPPLIER_VERIFICATION_PAY_STARTED:'supplier_verification_pay_started',

  // Admin supplier-application approval (Blocker 2 fix)
  ADMIN_APP_APPROVED:            'admin_application_approved',
  ADMIN_APP_REJECTED:            'admin_application_rejected',

  // Auth (activation funnel)
  AUTH_SIGNED_UP:                  'auth_signed_up',
  AUTH_SIGNED_IN:                  'auth_signed_in',
  AUTH_SIGNED_OUT:                 'auth_signed_out',
  AUTH_PASSWORD_RESET_REQUESTED:   'auth_password_reset_requested',
  AUTH_PASSWORD_RESET_COMPLETED:   'auth_password_reset_completed',
  AUTH_CONFIRMATION_RESENT:        'auth_confirmation_resent',

  // Recently viewed
  RECENTLY_VIEWED_CLICKED:   'recently_viewed_clicked',

  // Operational notifications
  NOTIFICATION_DISPATCHED:   'notification_dispatched',
  NOTIFICATION_FAILED:       'notification_failed',
  EMAIL_SENT:                'email_sent',
  EMAIL_DELIVERED:           'email_delivered',
  EMAIL_FAILED:              'email_failed',
  RFQ_CONFIRMATION_SENT:     'rfq_confirmation_sent',

  // RFQ tracking
  RFQ_TRACKING_VIEWED:       'rfq_tracking_viewed',
  RFQ_TRACKING_NOT_FOUND:    'rfq_tracking_not_found',
  RFQ_SUPPORT_CTA_CLICKED:   'rfq_support_cta_clicked',
  RFQ_TRACK_LOOKUP:          'rfq_track_lookup',
  RFQ_QR_GENERATED:          'rfq_qr_generated',
  RFQ_QR_DOWNLOADED:         'rfq_qr_downloaded',
  RFQ_TRACK_LINK_COPIED:     'rfq_track_link_copied',
  RFQ_TRACK_LINK_SHARED:     'rfq_track_link_shared',

  // Admin Operations Center
  RFQ_VIEWED:                'rfq_viewed',
  RFQ_ASSIGNED:              'rfq_assigned',
  RFQ_STATUS_UPDATED:        'rfq_status_updated',
  RFQ_NOTE_ADDED:            'rfq_note_added',
  RFQ_SUPPLIERS_ATTACHED:    'rfq_suppliers_attached',
  SUPPLIER_CLAIM_REVIEWED:   'supplier_claim_reviewed',

  // Supplier Portal
  SUPPLIER_CLAIM_SUBMITTED:  'supplier_claim_submitted',
  SUPPLIER_PROFILE_UPDATED:  'supplier_profile_updated',
  SUPPLIER_QUOTE_SUBMITTED:  'supplier_quote_submitted',
  SUPPLIER_INBOX_VIEWED:     'supplier_inbox_viewed',
  SUPPLIER_RFQ_OPENED:       'supplier_rfq_opened',
};

export const analyticsStatus = () => ({
  provider: PROVIDER,
  enabled: isEnabled(),
  dnt: respectsDoNotTrack(),
  hasKey: Boolean(KEY),
  prod: IS_PROD,
  debug: DEBUG,
});
